import { useQueryClient } from '@tanstack/react-query';
import { getHomePageUrl } from '../utils/auth-redirect';
import type { logout as logoutAction } from '../stores/slices/authSlice';

/**
 * useLogout Hook 配置
 */
export interface UseLogoutConfig {
  /** tRPC 实例 */
  trpc: any;
  
  /** Redux dispatch 函数 */
  dispatch: any;
  
  /** logout action creator */
  logout: typeof logoutAction;
  
  /**
   * 退出成功后的回调（可选）
   */
  onSuccess?: () => void;
  
  /**
   * 退出失败后的回调（可选）
   */
  onError?: (error: any) => void;
  
  /**
   * 是否在退出后跳转到 home 主页（默认 true）
   */
  redirectToHome?: boolean;
}

/**
 * useLogout 返回值
 */
export interface UseLogoutResult {
  /** 退出登录函数 */
  logout: () => void;
  
  /** 是否正在退出 */
  isLoggingOut: boolean;
}

/**
 * 统一退出登录 Hook
 * 
 * 职责：
 * 1. 调用后端 logout API 清除 Cookie
 * 2. 清除 Redux 状态
 * 3. 跳转到 home 主页
 * 
 * 优点：
 * - 统一的退出逻辑
 * - 避免在各个模块中重复代码
 * - 易于维护和升级
 * 
 * @example
 * ```tsx
 * import { useLogout, logout as logoutAction } from '@his/shared';
 * 
 * function AppNavbar() {
 *   const dispatch = useAppDispatch();
 *   const { logout, isLoggingOut } = useLogout({
 *     trpc,
 *     dispatch,
 *     logout: logoutAction,
 *   });
 * 
 *   return (
 *     <Button onClick={logout} loading={isLoggingOut}>
 *       退出登录
 *     </Button>
 *   );
 * }
 * ```
 */
export function useLogout(config: UseLogoutConfig): UseLogoutResult {
  const {
    trpc,
    dispatch,
    logout: logoutActionCreator,
    onSuccess,
    onError,
    redirectToHome = true,
  } = config;
  
  const queryClient = useQueryClient();
  
  // 调用后端 API 清除 Cookie
  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      console.log('✅ [useLogout] 退出登录成功，清除 Cookie');
      
      // ✅ 1. 后端已清除 Cookie（通过 API 调用）
      
      // ✅ 2. 清除 React Query 缓存（特别是 auth.me 的缓存）
      // 避免跳转后 AuthProvider 或 RouteGuard 尝试获取用户信息
      queryClient.clear();
      console.log('🧹 [useLogout] 已清除 React Query 缓存');
      
      // ✅ 3. 清除前端 Redux 状态（标记为未登录）
      // 必须在跳转前清除，避免 RouteGuard 拦截
      dispatch(logoutActionCreator());
      console.log('🧹 [useLogout] 已清除 Redux 状态');
      
      // ✅ 4. 立即跳转到 home 主页
      // 使用 window.location.replace() 立即跳转，中断所有后续代码执行
      if (redirectToHome && typeof window !== 'undefined') {
        const homePageUrl = getHomePageUrl();
        console.log('🔄 [useLogout] 跳转到 home 主页:', homePageUrl);
        // 使用 replace 立即跳转，这会中断所有后续代码执行，包括 React 渲染
        window.location.replace(homePageUrl);
        // 跳转后，后续代码不会执行，页面会立即跳转
        return; // 提前返回，确保后续代码不会执行
      }
      
      // ✅ 5. 如果不跳转，执行自定义回调
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: unknown) => {
      console.error('❌ [useLogout] 退出登录失败:', error);
      
      // ✅ 即使失败也清除状态和缓存
      queryClient.clear();
      dispatch(logoutActionCreator());
      
      // ✅ 即使失败也立即跳转
      if (redirectToHome && typeof window !== 'undefined') {
        const homePageUrl = getHomePageUrl();
        console.log('🔄 [useLogout] 跳转到 home 主页（失败情况）:', homePageUrl);
        window.location.replace(homePageUrl);
        return; // 提前返回，确保后续代码不会执行
      }
      
      // ✅ 执行自定义回调
      if (onError) {
        onError(error);
      }
    },
  });
  
  return {
    logout: () => logoutMutation.mutate(),
    isLoggingOut: logoutMutation.isPending,
  };
}

